import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Color mapping for operators
const operatorColors = {
  'Vodacom': '#E63946',
  'Airtel': '#1E3A8A',
  'Orange': '#FFA500',
  'Africell': '#32CD32',
  'Helios': '#800080',
  'East-Castle': '#FFD700',
  'TOA': '#00CED1'
};

// Round dot markers
const getMarkerIcon = (operator, isSelected) => {
  const color = operatorColors[operator] || '#3388ff';
  const borderColor = isSelected ? '#FF0000' : 'white';
  const size = isSelected ? 30 : 24;
  
  return L.divIcon({
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
      <circle cx="12" cy="12" r="10" fill="${color}" stroke="${borderColor}" stroke-width="2"/>
      ${isSelected ? '<circle cx="12" cy="12" r="12" stroke="#FF0000" stroke-width="2" fill="none" stroke-dasharray="4 4"/>' : ''}
    </svg>`,
    className: 'custom-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

// Map Controls Component
function MapControls({ onZoomChange }) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });

  return null;
}

// Legend Component
function MapLegend({ zoomLevel }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={`map-legend ${collapsed ? 'collapsed' : ''}`}>
      <div className="legend-header" onClick={() => setCollapsed(!collapsed)}>
        <span>🗺️ Légende</span>
        <span>{collapsed ? '▼' : '▲'}</span>
      </div>
      
      {!collapsed && (
        <div className="legend-content">
          <div className="zoom-info">Niveau de zoom: {zoomLevel}</div>
          <h4>Opérateurs</h4>
          <div className="legend-items">
            {Object.entries(operatorColors).map(([op, color]) => (
              <div key={op} className="legend-item">
                <span className="color-dot" style={{ backgroundColor: color }}></span>
                <span>{op}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Popup Component
const SitePopup = ({ site, isSelected, onSiteSelect }) => {
  const color = operatorColors[site.operator] || '#1E3A8A';
  
  return (
    <div className="site-popup">
      <h3 style={{ color, borderBottom: `2px solid ${color}` }}>{site.site_name}</h3>
      <p><strong>SAP ID:</strong> {site.sap_id}</p>
      <p><strong>Opérateur:</strong> {site.operator}</p>
      <p><strong>Province:</strong> {site.province}</p>
      <p><strong>Type:</strong> {site.type}</p>
      <p><strong>Catégorie:</strong> {site.category}</p>
      <p><strong>Infrastructure:</strong> {site.infrastructure}</p>
      <p><strong>Tour:</strong> {site.has_tower ? 'Oui' : 'Non'}</p>
      {site.height && <p><strong>Hauteur:</strong> {site.height}m</p>}
      <p><strong>Date:</strong> {site.on_air_date}</p>
      <button 
        className="btn-select"
        onClick={() => onSiteSelect(site.id)}
        style={{ backgroundColor: color }}
      >
        {isSelected ? '✓ Désélectionner' : '+ Sélectionner'}
      </button>
    </div>
  );
};

// Map Style Selector
function MapStyleSelector({ mapStyle, setMapStyle }) {
  return (
    <div className="map-style-selector">
      <button 
        className={mapStyle === 'streets' ? 'active' : ''}
        onClick={() => setMapStyle('streets')}
      >🏙️ Rues</button>
      <button 
        className={mapStyle === 'satellite' ? 'active' : ''}
        onClick={() => setMapStyle('satellite')}
      >🛰️ Satellite</button>
      <button 
        className={mapStyle === 'terrain' ? 'active' : ''}
        onClick={() => setMapStyle('terrain')}
      >⛰️ Terrain</button>
    </div>
  );
}

// Main Map Component
const Map = ({ sites = [], selectedSites = [], onSiteSelect }) => {
  const [zoomLevel, setZoomLevel] = useState(6);
  const [mapStyle, setMapStyle] = useState('streets');
  const position = [-2.5, 23.5];

  const mapStyles = {
    streets: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    terrain: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'
  };

  return (
    <div className="map-wrapper">
      <MapStyleSelector mapStyle={mapStyle} setMapStyle={setMapStyle} />
      <MapLegend zoomLevel={zoomLevel} />
      
      <div className="stats-badge">
        <span>📊 {sites.length} sites</span>
      </div>

      <MapContainer 
        center={position} 
        zoom={6} 
        style={{ height: '600px', width: '100%', borderRadius: '12px' }}
      >
        <ZoomControl position="bottomright" />
        <MapControls onZoomChange={setZoomLevel} />
        
        <TileLayer url={mapStyles[mapStyle]} />
        
        {sites.map((site) => {
          const isSelected = selectedSites.includes(site.id);
          return (
            <Marker 
              key={site.id} 
              position={[site.latitude, site.longitude]}
              icon={getMarkerIcon(site.operator, isSelected)}
              eventHandlers={{ click: () => onSiteSelect(site.id) }}
            >
              <Popup>
                <SitePopup 
                  site={site}
                  isSelected={isSelected}
                  onSiteSelect={onSiteSelect}
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default Map;
